import {Link} from "react-router-dom";
import styles from "../Year/Year.module.css";
import {usePermissions} from "../../hooks/usePermissions";
import {AskQuestion} from "./AskQuestion";

const GOLD = "var(--app-gold)";

const Section = ({id, title, children}) => (
    <section id={id} style={{scrollMarginTop: "20px"}} className="mt-5">
        <h3 style={{color: GOLD, borderBottom: `2px solid ${GOLD}`, paddingBottom: "6px"}}>{title}</h3>
        <div className="mt-3">{children}</div>
    </section>
);

const Term = ({name, children}) => (
    <tr>
        <td style={{fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top"}}>{name}</td>
        <td>{children}</td>
    </tr>
);

export const Help = () => {
    // Ж8: разделът „Администрация" се показва САМО на администратори. Обикновеният
    // потребител няма достъп до панела и не е нужно да знае, че съществува —
    // в ръководството това е само шум (и излишна информация за чужд екран).
    const {isAdmin} = usePermissions();

    return (
        <div style={{maxWidth: "1000px", margin: "0 auto", paddingBottom: "60px"}}>
            <div className="mt-3">
                <h2 className="btn btn-outline-secondary" style={{marginRight: "10px"}}>
                    <Link className={styles['button-link']} to={'/'}>Начало</Link>
                </h2>
            </div>

            <div className="d-flex flex-column align-items-center mt-3">
                <h1 style={{color: GOLD, textAlign: "center"}}>Ръководство за употреба</h1>
                <p className="text-muted" style={{textAlign: "center", maxWidth: "700px"}}>
                    APP 4 FARMERS — система за управление на земеделска земя по землища, ползватели и
                    стопански години. Тук е описано какво прави всеки екран и как да го използвате.
                </p>
            </div>

            {/* Съдържание */}
            <div className="card mt-4" style={{borderColor: GOLD}}>
                <div className="card-header" style={{color: GOLD, fontWeight: 600}}>Съдържание</div>
                <div className="card-body">
                    <ol className="mb-0" style={{columns: 2, columnGap: "40px"}}>
                        <li><a href="#overview">Общ преглед</a></li>
                        <li><a href="#glossary">Речник на понятията</a></li>
                        <li><a href="#login">Вписване в системата</a></li>
                        <li><a href="#home">Начален екран и избор на година</a></li>
                        <li><a href="#upload">Зареждане на първоначални данни (CSV)</a></li>
                        <li><a href="#year-nav">Навигация</a></li>
                        <li><a href="#settlements">Землища</a></li>
                        <li><a href="#companies">Ползватели</a></li>
                        <li><a href="#company-info">Карта на ползвател</a></li>
                        <li><a href="#deals">Създаване на ДЗ (замяна)</a></li>
                        <li><a href="#others">БП и пътища · Други площи</a></li>
                        <li><a href="#drawings">Изчертаване</a></li>
                        <li><a href="#archive">Архивиране (сваляне на CSV)</a></li>
                        <li><a href="#copy">Копиране от предходна година</a></li>
                        <li><a href="#reports">Справки С1–С14</a></li>
                        <li><a href="#declarations">Сравнение на декларации</a></li>
                        {isAdmin && <li><a href="#admin">Администрация</a></li>}
                        <li><a href="#tips">Съвети и чести въпроси</a></li>
                        <li><a href="#ask">Въпрос към администратора</a></li>
                    </ol>
                </div>
            </div>

            {/* 1. Общ преглед */}
            <Section id="overview" title="1. Общ преглед">
                <p>
                    Приложението проследява <strong>земеделска земя</strong>, декларирана от ползватели
                    (фирми или физически лица), организирана по <strong>землища</strong> и по{" "}
                    <strong>стопанска година</strong>. За всяко землище и ползвател системата съхранява
                    декларираните площи, прехвърлянията на права между ползватели (ДЗ), допълнителните
                    площи (бели петна, пътища, корекции) и реалното изчертаване на масивите с култури.
                </p>
                <p>Работата обикновено следва тази последователност:</p>
                <ol>
                    <li>Влизате в системата и избирате <strong>стопанска година</strong>.</li>
                    <li>Зареждате <strong>първоначалните данни</strong> за землищата от CSV файл.</li>
                    <li>Въвеждате <strong>ДЗ</strong> (прехвърляния), <strong>други площи</strong> и{" "}
                        <strong>изчертавания</strong> за всеки ползвател.</li>
                    <li>Извеждате <strong>справки</strong> (С1–С14) на екран или в PDF.</li>
                </ol>
            </Section>

            {/* 2. Речник */}
            <Section id="glossary" title="2. Речник на понятията">
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <tbody>
                        <Term name="Ползвател">Фирма или физическо лице, което декларира и обработва земя.
                            Идентифицира се по Булстат/ЕГН.</Term>
                        <Term name="Булстат / ЕИК">Уникален идентификатор на ползвателя (ЕИК на фирма или ЕГН
                            на физическо лице).</Term>
                        <Term name="Землище / EKATTE">Населено място със земеделски землищни граници. EKATTE е
                            уникалният му код в националния регистър.</Term>
                        <Term name="Стопанска година">Земеделската година — от 1 октомври до 30 септември.
                            Всички данни са организирани по стопанска година. Годината в системата
                            (при качване на файлове, в URL адреса и в справките) е <strong>стопанската</strong>,
                            а не календарната — напр. през юли 2026 г. текущата стопанска година е 2027.</Term>
                        <Term name="Декларирана">Общата площ земя (в декари), която ползвателят е декларирал в
                            землището.</Term>
                        <Term name="Необработваема / Негодна">Част от декларираната земя, която не може да се
                            обработва.</Term>
                        <Term name="Годна">Обработваемата част = Декларирана − Необработваема (коригирана с
                            корекцията).</Term>
                        <Term name="ДЗ">Доброволна замяна — прехвърляне на площ
                            между двама ползватели. Всяка ДЗ създава запис при даващия и при взимащия.</Term>
                        <Term name="Чакаща замяна">Замяна към ДРУГО землище, която още не е одобрена от
                            потребителя на онова землище. Влиза в сила само в землището, където е
                            създадена.</Term>
                        <Term name="Отказана замяна">Замяна, която потребителят на насрещното землище не е
                            приел. Остава в сила там, където е създадена — отказът не отменя нищо.</Term>
                        <Term name="Неопределено землище">Псевдо-землище по община, което се ползва, когато
                            конкретното село още не е известно. Замяна към него влиза в сила веднага —
                            няма кой да я одобри.</Term>
                        <Term name="Бели петна (БП)">Площи без установен собственик, разпределени към
                            ползвателя.</Term>
                        <Term name="Пътища">Площи от пътища, добавени към ползването.</Term>
                        <Term name="Други">Допълнителни площи с индивидуална забележка (напр. корекции).</Term>
                        <Term name="Корекция">Коригираща площ, която се изважда от годната земя (и се добавя към
                            необработваемата) при изчисленията.</Term>
                        <Term name="Правно">Правно основаната площ за изчертаване =
                            Годна − Корекция + ДЗ + Бели петна + Пътища − Други.</Term>
                        <Term name="Изчертано">Сумата на реално изчертаните масиви (площи) за ползвателя в
                            землището.</Term>
                        <Term name="Масив">Номер на земеделския масив.</Term>
                        <Term name="МПО номер">Номер на масива за ползване (МПО) на конкретния имот.</Term>
                        <Term name="Култура">Отглежданата култура върху изчертаната площ.</Term>
                        <Term name="Забележка">Свободен текст към ЕДИН изчертан ред (масив + площ).
                            Вижда се в справките с изчертани площи и през публичния линк, тоест я чете и
                            самият ползвател.</Term>
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* 3. Login */}
            <Section id="login" title="3. Вписване в системата">
                <ol>
                    <li>Въведете <strong>потребителско име</strong> и <strong>парола</strong>.</li>
                    <li>Отметнете съгласието с <Link to="/terms-of-service" target="_blank">Общите условия</Link> и{" "}
                        <Link to="/privacy-policy" target="_blank">Политиката за поверителност</Link> — бутонът
                        „Влез" се активира едва след това.</li>
                    <li>Натиснете <strong>Влез</strong>.</li>
                </ol>
                <p className="text-muted">Достъпът до системата е защитен — без валидно вписване екраните с
                    данни не се зареждат.</p>
                <p><strong>Изход при бездействие.</strong> След <strong>40 минути</strong> без работа
                    (без мишка, клавиатура или превъртане) системата ви изписва сама — за да не
                    остане отворена на оставен компютър{isAdmin && " (администраторите не се изписват)"}.
                    Две минути преди това излиза предупреждение с обратно броене; натиснете{" "}
                    <strong>„Оставам"</strong>, за да продължите. Времето се брои общо за всички
                    отворени раздели на браузъра. Незаписаното във формите се губи — записвайте,
                    преди да станете от компютъра.</p>
            </Section>

            {/* 4. Home */}
            <Section id="home" title="4. Начален екран и избор на година">
                <p>След вписване се озовавате на началния екран. Оттук:</p>
                <ul>
                    <li>Изберете <strong>стопанска година</strong> от падащия списък и натиснете{" "}
                        <strong>Влез</strong>, за да отворите землищата за тази година.</li>
                    <li><strong>„Първоначални данни"</strong> в горната лента отваря екрана за
                        зареждане, редакция, архивиране и изтриване на данни.</li>
                </ul>
            </Section>

            {/* 5. Upload */}
            <Section id="upload" title="5. Зареждане на първоначални данни (CSV)">
                <p>Екранът <strong>„Първоначални данни"</strong> има шест части: качване на правно
                    основание и под него <strong>два масови вноса от стари бази</strong> (доброволни
                    замени и изчертани площи), после редакция, <a href="#archive">архивиране</a> и
                    изтриване.</p>

                <h6 className="fw-bold mt-3">5.1 Зареждане на файл</h6>
                <p>Изберете CSV файл и натиснете <strong>Зареди</strong>, за да го прегледате, след което{" "}
                    <strong>Качи в базата</strong>. Файлът трябва да е с колони точно в тази
                    последователност (на латиница):</p>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>bullstat</th>
                            <th>company_name</th>
                            <th>declared_land</th>
                            <th>unsuitable_land</th>
                            <th>suitable_land</th>
                            <th>year</th>
                            <th>settlement_ekatte</th>
                        </tr>
                        </thead>
                    </table>
                </div>
                <ul>
                    <li><strong>Инкрементално качване:</strong> добавят се само новите редове. Ако ред за
                        същата комбинация <em>година + булстат + EKATTE</em> вече съществува, той се{" "}
                        <strong>прескача</strong> (не се дублира). Не е нужно да триете преди повторно
                        качване.</li>
                    <li>След качване се показва <strong>резюме</strong>: добавени, пропуснати (вече
                        съществуващи) и грешки.</li>
                    <li>Новите ползватели и EKATTE кодове се проверяват — несъществуващ EKATTE код блокира
                        качването.</li>
                    <li>Разделителят трябва да е <strong>запетая</strong>. Файлът се разчита коректно и при
                        кирилица, записана в Windows-1251 (ANSI).</li>
                </ul>
                <p className="text-muted">Подробни указания и видео има на връзката{" "}
                    <Link to="/upload-help" target="_blank">„Указания за качване на файл с данни"</Link>.</p>

                <h6 className="fw-bold mt-3">5.2 Редакция на данни в базата</h6>
                <p>Изберете <strong>година</strong> и <strong>EKATTE</strong>, натиснете <strong>Зареди</strong>{" "}
                    и редактирайте съществуващите редове директно в таблицата. Качването на файл{" "}
                    <strong>не променя</strong> вече съществуващи редове — за поправки използвайте тази
                    секция.</p>

                <h6 className="fw-bold mt-3">5.3 Изтриване на данни от базата</h6>
                <p>Изберете <strong>година</strong> и <strong>EKATTE</strong> и натиснете{" "}
                    <strong>Изтрий от базата</strong>, за да премахнете първоначалните данни за цяло
                    землище за тази година. Действието е необратимо — използвайте го внимателно.</p>

                <h6 className="fw-bold mt-3">5.4 Масов внос от стари бази</h6>
                <p>Двете секции — <strong>„Доброволни замени (стари бази)"</strong> и{" "}
                    <strong>„Изчертани площи (стари бази)"</strong> — служат за наливане на данни от
                    предишната система, където ползвателите са по <strong>име</strong>, а не по булстат.</p>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr><th>Файл</th><th>Колони</th></tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>Доброволни замени</td>
                            <td><code>Даващ; Взимащ; За село; Сума; Година; ЕКТТ</code></td>
                        </tr>
                        <tr>
                            <td>Изчертани площи</td>
                            <td><code>Ползвател; Масив; МПО Номер; Площ; Година; ЕКТТ</code></td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <ul>
                    <li><strong>Първо качете правното основание</strong> за същото землище и година —
                        то е единственото място, където имената се срещат с булстатите.</li>
                    <li>Един файл е за <strong>едно землище и една година</strong>.</li>
                    <li><strong>Прегледът е задължителна стъпка.</strong> Преди да се запише каквото и
                        да е, екранът показва: колко реда са разпознати, кои имена не са (с предложение
                        за най-близкото), кои повтарящи се редове са слети и с каква сума, какви
                        конфликти има и каква е общата площ.</li>
                    <li>Повтарящите се замени (един и същ даващ, взимащ и „За село") се{" "}
                        <strong>сумират</strong>. Това е и причината повторното качване на същия файл
                        да не дублира нищо.</li>
                    <li>Ред с <strong>празна площ или площ 0</strong> в изчертаните не се качва.</li>
                    <li>Непознато име <strong>блокира</strong> реда — нищо не влиза наслуки.</li>
                    <li><strong>„Замести съществуващите"</strong> изтрива данните за тази година и
                        землище, преди да запише файла. По подразбиране е изключено.</li>
                    <li>Всяко качване е <strong>партида</strong> и може да се{" "}
                        <strong>отмени наведнъж</strong> от списъка „Предишни качвания". Изтритото при
                        заместване обаче не се възстановява.</li>
                    <li>Файловете от старата система са в Windows-1251 — не е нужно да ги
                        преобразувате.</li>
                </ul>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    Ако колоната „За село" съдържа различни землища „неопределено", вносът спира и
                    казва защо: това почти винаги е клетка, издърпана надолу в Excel.
                </p>
            </Section>

            {/* 6. Year navigation */}
            <Section id="year-nav" title="6. Навигация">
                <p>Навигацията е обща за цялата система — <strong>горна лента</strong> и{" "}
                    <strong>пътечка</strong> под нея:</p>
                <ul>
                    <li><strong>Горна лента</strong> — най-вляво е бутонът с <strong>годината</strong>:
                        падащо меню с всички стопански години и „Начална страница". След него са
                        основните раздели: <em>Землища</em>, <em>Ползватели-редакция</em>,{" "}
                        <em>Справки</em>, <em>Първоначални данни</em>, <em>Помощ</em>
                        {isAdmin && <>, а за администратори и <em>Администрация</em></>}. Активният
                        раздел е подчертан.</li>
                    <li><strong>Пътечка</strong> (breadcrumb) — показва къде се намирате (напр.{" "}
                        <em>2027 › Землища › общ. Генерал Тошево › с.Изворово › Ползвател</em>); всяка
                        стъпка е връзка назад. Стъпката <strong>„общ. …"</strong> отваря списъка със
                        землища с вече разгъната тази община — за да не се търси наново.</li>
                    <li>В контекст на землище или ползвател под пътечката се появяват и{" "}
                        <strong>локални табове</strong> (в землище: Ползватели / ДЗ / С1 / БП и пътища /
                        Сравнение на декларации; в картата на ползвател: ДЗ / Изчертаване / БП и пътища /
                        Други площи / … / бързи справки).</li>
                    <li>Вдясно в лентата: превключване на <strong>светла/тъмна тема</strong> (изборът
                        се помни на този компютър), <strong>Назад</strong> — една стъпка назад, и{" "}
                        <strong>Изход</strong>.</li>
                </ul>
                <p className="text-muted">В списъка с ползватели в едно землище редовете са подредени{" "}
                    <strong>низходящо по изчертана площ</strong> (най-изчертаните отгоре).</p>
            </Section>

            {/* 7. Settlements */}
            <Section id="settlements" title="7. Землища">
                <p>В списъка с землища всеки ред показва EKATTE, селище, община и област. При избор на
                    землище се отварят табовете:</p>
                <ul>
                    <li><strong>Ползватели</strong> — две таблици: ползвателите, които{" "}
                        <em>първоначално са декларирали</em> земя в землището, и тези, които са{" "}
                        <em>получили</em> земя чрез ДЗ. Всяка колона (Декларирана, Необработваема, Годна,
                        ДЗ, БП, Правно, Изчертано) показва обобщените стойности. Клик върху ползвател
                        отваря неговата карта.</li>
                    <li><strong>ДЗ</strong> — всички направени прехвърляния в това землище до момента.</li>
                    <li><strong>С1 — Справка на землище</strong> и <strong>БП и пътища</strong> (справка
                        С13 за землището) — направо оттук, без да се минава през „Справки".</li>
                    <li><strong>Сравнение на декларации</strong> — виж{" "}
                        <a href="#declarations">раздел 15</a>.</li>
                </ul>
            </Section>

            {/* 8. Companies */}
            <Section id="companies" title="8. Ползватели">
                <p>Екранът <strong>„Ползватели общо"</strong> позволява:</p>
                <ul>
                    <li><strong>Прибави ползвател</strong> — форма за нов ползвател. Задължителни са{" "}
                        <em>Фирма</em>, <em>Лице за контакт</em> и <em>Булстат/ЕГН</em>; останалите
                        (адрес, град, телефон, забележка) са по избор.</li>
                    <li><strong>Търсене</strong> на съществуващ ползвател по име, град и булстат. При избор
                        се показват данните му за редакция и обобщение на неговите ДЗ по землища.</li>
                </ul>
            </Section>

            {/* 9. Company Info */}
            <Section id="company-info" title="9. Карта на ползвател">
                <p>Картата показва данните за <strong>един ползвател в едно землище за избрана
                    година</strong>. Най-горе стоят землището и името на ползвателя с ЕИК, а под тях —
                    трите изходни числа (<strong>Декларирана</strong>, <strong>Необработваема</strong>,{" "}
                    <strong>Годна</strong>) на повдигнати плочки, за да се хващат с един поглед.
                    В горния десен ъгъл има две табла:</p>
                <ul>
                    <li><strong>Правно</strong> — правно основаната площ за изчертаване.</li>
                    <li><strong>Изчертано</strong> — сумата на вече изчертаните площи.</li>
                </ul>
                <p>Формулата за правно основаната площ е:</p>
                <div className="alert" style={{background: "var(--app-surface-2)", border: `1px solid ${GOLD}`}}>
                    <strong>Правно = Годна − Корекция + ДЗ + Бели петна + Пътища − Други</strong>
                </div>
                <p>По-надолу са таблиците с данните (Декларирана/Необработваема/Годна, Други площи, ДЗ,
                    Изчертавания). Действията са в <strong>локалните табове</strong> горе, под
                    пътечката; формата на избрания таб се отваря под таблиците:</p>
                <ul>
                    <li><strong>ДЗ</strong> — нова замяна (прехвърляне).</li>
                    <li><strong>Изчертаване</strong> — добавяне на изчертан масив.</li>
                    <li><strong>БП и пътища</strong> — белите петна и пътищата. Докато екранът не е
                        минат през човек, на таба стои червен <strong>„!"</strong> — признакът е
                        „прегледано", не „има стойност", затова гасне и при запис на нули.</li>
                    <li><strong>Други площи</strong> — Други(−), Други(+) и корекция.</li>
                    <li><strong>Данни ползвател</strong> — редакция на данните за контакт.</li>
                    <li><strong>Копиране</strong> — пренасяне на данни от предходна година.</li>
                    <li><strong>Бързи справки</strong> — „Пълна справка" (С3), „Обща справка" (С4) и
                        „Контролна справка" (С9) за текущия контекст.</li>
                </ul>
                <h6 className="fw-bold mt-3">Панелът с информация за ползвателя</h6>
                <p>Иконата пред името на ползвателя (в списъка на землището и в списъка със замените)
                    отваря панел с фирмата, лицето, адреса и телефона. Ако номерът е{" "}
                    <strong>ЕИК на фирма</strong> (9 или 13 цифри), той е връзка към търсене — оттам
                    излизат адресът на регистрация и управителят. Под него има и връзка към самата форма
                    „Справка по физическо или юридическо лице" в Търговския регистър: при натискане
                    номерът се копира в паметта и остава само да се постави в полето
                    ЕИК/ПИК/БУЛСТАТ.</p>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    При ползвател — физическо лице (10-цифрен номер, ЕГН) връзка НЕ се предлага: фирмен
                    регистър няма какво да покаже по ЕГН, а и личният номер не бива да излиза към
                    външна търсачка. Бутонът „копирай" работи и там.
                </p>
            </Section>

            {/* 10. Deals */}
            <Section id="deals" title="10. Създаване на ДЗ (замяна)">
                <p>Формата за нова ДЗ изисква:</p>
                <ul>
                    <li><strong>Даващ</strong> — ползвателят, който прехвърля площ.</li>
                    <li><strong>Взимащ</strong> — ползвателят, който получава площта.</li>
                    <li><strong>За село</strong> — землището, за което важи прехвърлянето.</li>
                    <li><strong>Сума</strong> — площта в декари (минимум 0,01).</li>
                </ul>
                <p>Системата <strong>проверява наличното</strong> и на даващия, и на взимащия, и предупреждава
                    в червено, ако сумата надвишава наличната площ. Даващият и взимащият не могат да са едно и
                    също лице.</p>
                <p>Всяка ДЗ се записва при даващия и при взимащия. В таблицата с ДЗ всеки ред може да бъде:</p>
                <ul>
                    <li><strong>Редактиран</strong> — промяна на сумата <em>и на двете землища</em>
                        (даващо и взимащо);</li>
                    <li><strong>Изтрит</strong> (с потвърждение),</li>
                    <li>или да му се <strong>генерира PDF протокол</strong>. След генериране бутонът се
                        сменя с връзка за изтегляне на протокола.</li>
                </ul>

                <h5 style={{color: GOLD}} className="mt-4">Замяна към друго землище и одобрение</h5>

                <p>В полето <strong>„За село"</strong> може да изберете <strong>всяко активно землище</strong>,
                    включително такова, за което нямате права да чертаете. Землищата се разпределят между
                    няколко души, а замените редовно пресичат тези граници.</p>

                <p>Когато посочите <strong>друго</strong> землище, замяната:</p>
                <ul>
                    <li><strong>влиза в сила веднага</strong> в землището, в което я създавате — там нищо
                        не чака и можете да чертаете още същия ден;</li>
                    <li><strong>чака одобрение</strong> в посоченото землище. Дотогава тя не влиза в
                        неговото правно основание, в наличното за чертане и в справките му.</li>
                </ul>

                <p>Одобрява потребителят, на когото е зададено това землище, или администраторът.
                    Одобрение се иска <strong>винаги</strong>, когато землището е друго — дори двете
                    землища да са ваши. Замяна <strong>в същото землище</strong> или към
                    <strong> „неопределено"</strong> землище влиза в сила веднага, без одобрение.</p>

                <h6 className="fw-bold mt-3">Как изглежда</h6>
                <ul>
                    <li>Редът на замяната е оцветен, докато чака, и носи пояснение —
                        „чака потвърждение от с. Х" при вас, „чака вашето потвърждение" отсреща.</li>
                    <li>В списъка с ползватели на землището пред името светва <strong>червена
                        икона</strong>. Замяната засяга две фирми, затова иконата свети при двете;
                        одобрението е едно и гаси и двете.</li>
                    <li>До <strong>„Землища"</strong> в горната лента стоят две значки: жълта за замени,
                        които чакат вашето одобрение, и червена за ваши замени, отказани отсреща.</li>
                </ul>

                <h6 className="fw-bold mt-3">Ако замяната бъде отказана</h6>
                <p><strong>Отказът не отменя нищо.</strong> Замяната остава в сила в землището, където
                    сте я създали — така, както сте я записали. В посоченото землище просто никога не
                    влиза. Редът остава с пояснение „не е потвърдена в с. Х" <strong>завинаги</strong>,
                    за да не изглежда след месец като нормална одобрена замяна.</p>
                <p>Това означава, че след отказ числата на двете страни <strong>се разминават</strong>.
                    Така е решено съзнателно: отказът може да дойде след седмици, а автоматичното връщане
                    на сметките би променило землище, което отдавна е приключено. Разбирате се по телефон
                    с колегата си и със земеделците и, ако трябва, се връщате и поправяте замяната.</p>
                <p>Когато въпросът е уреден, натиснете <strong>„Уредено"</strong> на реда — това гаси само
                    червената сигнализация. Отказана замяна може да бъде <strong>одобрена по-късно</strong>,
                    ако се окаже, че отказът е бил прибързан.</p>

                <h6 className="fw-bold mt-3">Редакция, изтриване, протокол</h6>
                <ul>
                    <li><strong>Редакция</strong> на вече одобрена замяна я връща за ново одобрение —
                        числата отсреща се променят и някой трябва да ги приеме пак.</li>
                    <li>Потребителят на посоченото землище може да <strong>изтрие</strong> вече одобрена
                        замяна. Тогава при вас редът остава <strong>блед, с надпис „изтрит"</strong>, за
                        да разберете; окончателното изтриване е ваше решение.</li>
                    <li><strong>Протоколът се вади веднага</strong>, без да чака одобрение — земеделците
                        са при вас днес. Докато замяната чака, върху протокола пише едро, че не е
                        окончателна. След одобрение го извадете наново и излиза чист.</li>
                </ul>

                <h6 className="fw-bold mt-3">Наличност в чуждо землище</h6>
                <p>При избор на насрещна фирма виждате <strong>само наличната ѝ площ</strong> в посоченото
                    землище — колкото да прецените дали замяната е коректна. Нищо друго за това землище не
                    ви се показва. Ако землището няма въведени данни, пише „Няма информация за наличности"
                    и замяната пак може да се направи.</p>

                <h6 className="fw-bold mt-3">Вериги от замени</h6>
                <p>Възможно е ползвателят в посоченото землище още да няма земя там и първо да трябва да
                    вземе от друг. Тогава една замяна чака друга. Системата не ги подрежда: ако одобрите
                    втората преди първата, ползвателят ще излезе с повече изчертано от правно и ще светне
                    в червено, докато и първата не бъде одобрена. Това не е грешка.</p>
            </Section>

            {/* 11. Others */}
            <Section id="others" title="11. БП и пътища · Други площи">
                <p>Допълнителните площи са разделени на <strong>два екрана</strong>, защото се въвеждат
                    в различни моменти от работата.</p>

                <h5 style={{color: GOLD}}>Таб „БП и пътища"</h5>
                <ul>
                    <li><strong>Бели петна</strong> и <strong>Пътища</strong> — <strong>добавят</strong> се
                        към правно основаната площ.</li>
                    <li>Докато не запишете, на таба свети <strong style={{color: "#d33"}}>червен „!"</strong>.
                        Той изчезва след запис — <strong>включително когато стойностите са нули</strong>.
                        Смисълът е „екранът е минал през вас", а не „има ли стойност"; така не се пропуска
                        в работния процес.</li>
                    <li>Ако след това промените стойностите, знакът не се появява отново.</li>
                </ul>

                <h5 style={{color: GOLD}}>Таб „Други площи"</h5>
                <ul>
                    <li><strong>Други(+)</strong> — <strong>увеличава</strong> правно основаната площ.</li>
                    <li><strong>Други(−)</strong> — <strong>намалява</strong> я. (Това е полето, което
                        досега се наричаше само „Други".)</li>
                    <li><strong>Забележка</strong> към двете „Други".</li>
                    <li><strong>Корекция</strong> (със забележка) — намалява годната земя и увеличава
                        необработваемата. Работи както преди; когато е въведена, табът „Други площи"
                        е подцветен бледо червено.</li>
                </ul>
                <p>Двата екрана пишат само своите полета — запис на един от тях не променя стойностите
                    от другия. Ако за ползвателя още няма ред с други площи, той се създава сам при
                    първия запис.</p>
                <p>Обобщаващият ред на картата на ползвателя показва{" "}
                    <strong>Пътища · Бели петна · Други(+) · Други(−)</strong>, а бутонът вдясно отваря
                    формата за „Други площи".</p>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    „Други" не се показва в справките — участва само в сметката за правното основание.
                </p>
            </Section>

            {/* 12. Drawings */}
            <Section id="drawings" title="12. Изчертаване">
                <p>Всеки изчертан масив се въвежда с: <strong>Масив</strong>, <strong>МПО номер</strong>,{" "}
                    <strong>Площ</strong> (декари) и <strong>Култура</strong>. Полето{" "}
                    <strong>Налично</strong> показва колко площ остава за изчертаване
                    (Правно − вече изчертано):</p>
                <ul>
                    <li><span style={{color: "var(--app-success-fg)", fontWeight: 600}}>Зелено</span> — има свободна площ
                        (или е почти напълно изчертано).</li>
                    <li><span style={{color: "var(--app-danger-fg)", fontWeight: 600}}>Червено</span> — изчертано е повече от
                        наличното; показва се отрицателната стойност (напр. −12.4).</li>
                </ul>
                <p>Изчертаните редове могат да се редактират и изтриват от таблицата
                    „Изчертани площи".</p>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    Навсякъде, където бутонът за редакция отваря ред с полета, до „Запази" има и{" "}
                    <strong>„Затвори"</strong>. Той не записва нищо и връща полетата такива, каквито са
                    били — за случая „отворих грешния ред" или „промених половината и се отказах".
                </p>

                <h5 style={{color: GOLD}}>Бутон „Проверено"</h5>
                <p>Масивът, МПО номерът и площта се четат от друга програма и после се сверяват тук.
                    Срещу всеки ред, след редакцията и триенето, стои бутон <strong>„Проверено"</strong>:</p>
                <ul>
                    <li>натискате го, когато сте сверили реда — редът застава на{" "}
                        <strong>блед фон</strong>, а бутонът става <strong>„За проверка"</strong>;</li>
                    <li>натиснете го отново, за да върнете реда за проверка;</li>
                    <li>мишката върху реда показва <strong>кой и кога</strong> го е сверил;</li>
                    <li>над таблицата пише <strong>„проверени N от M"</strong> — докъде е стигнала
                        проверката, без да броите редове.</li>
                </ul>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    Отметката е само за отчетност — не променя площи и не влиза в справките.
                    Новите редове (въведени на ръка, внесени от файл или копирани от предходна
                    година) тръгват като непроверени.
                </p>

                <h5 style={{color: GOLD}}>Забележка към ред</h5>
                <p>Между кошчето и „Проверено" стои бутон с балонче — <strong>„Забележка"</strong>.
                    Отваря ред с едно поле; записва се със „Запази", отказва се със „Затвори".
                    Иконата казва има ли нещо записано:</p>
                <ul>
                    <li><strong>бледа, само с контур</strong> — няма забележка;</li>
                    <li><strong>плътна, черна</strong> — има записана забележка.</li>
                </ul>
                <p>Текстът се вижда и направо на реда, в колона <strong>„Забележка"</strong> след
                    „Култура". Може да се коригира и от двете места — от бутона с балончето и от
                    бутона за редакция на реда. Копира се и при копиране на изчертавания от
                    предходна година.</p>
                <div className="alert" style={{background: "var(--app-surface-2)", border: `1px solid ${GOLD}`}}>
                    <strong>Внимание:</strong> забележката влиза в справките С3, С4, С8 и С11 (на екран
                    и в PDF) и се вижда <strong>и през публичния линк</strong> — тоест я чете самият
                    ползвател. Не е място за вътрешни бележки от рода на „съмнително, да се провери".
                </div>

                <h5 style={{color: GOLD}}>Прехвърляне на ред към друг ползвател</h5>
                <p>В реда за редакция, под колоната „Забележка", има падащ списък{" "}
                    <strong>„— прехвърли на…"</strong> с ползвателите, които работят в{" "}
                    <strong>същото землище през същата година</strong>. Изберете ли някого, бутонът
                    става <strong>„Прехвърли"</strong> и приложението пита, преди да го направи: редът
                    изчезва от този ползвател и се появява при другия, а площта влиза в неговото
                    изчертано.</p>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    Ползвател без данни в това землище не се предлага и не се приема — иначе редът би
                    изчезнал от екрана, на който го правите, и би се появил там, където никой не го
                    търси.
                </p>
            </Section>

            {/* 12.5 Архивиране */}
            <Section id="archive" title="12.5. Архивиране (сваляне на CSV)">
                <p>В <strong>„Първоначални данни"</strong>, между „Редакция" и „Изтриване", стои
                    секцията <strong>„Архивиране"</strong>. Избирате година и землище и сваляте два
                    файла: <strong>доброволни замени</strong> и <strong>изчертани площи</strong>.</p>
                <p>Колоните са същите като при качването на стари бази, затова свален файл може да се
                    качи обратно без преработка. В замените влизат всички, които засягат землището — и
                    дадените, и получените; <strong>отказаните и чакащите одобрение не влизат</strong>.
                    Ползвателите са по име.</p>
                <h6 className="fw-bold mt-3">Цяла община наведнъж</h6>
                <p>След годината се избира <strong>„Едно землище"</strong> или{" "}
                    <strong>„Цяла община"</strong>. При община приложението показва кои землища имат
                    данни и по колко реда, а бутонът сваля <strong>един ZIP файл</strong> с папка на
                    всяко землище и двата файла вътре.</p>
                <p className="text-muted" style={{fontSize: "0.9rem"}}>
                    Землище без замени получава само файла с изчертаните (и обратно) — празни файлове
                    не влизат, за да личи къде наистина няма данни. Псевдо-землището „неопределено"
                    също влиза, защото и в него има вписани замени.
                </p>
                <p className="text-muted">Тези файлове са ваш архив. Те не заместват резервните копия на
                    приложението, които се правят всяка нощ на сървъра.</p>
            </Section>

            {/* 13. Copy */}
            <Section id="copy" title="13. Копиране от предходна година">
                <p>Секцията <strong>„Копиране"</strong> пренася данни от предходната стопанска година за
                    текущия ползвател и землище:</p>
                <ul>
                    <li><strong>Копирай изчертавания</strong> — пренася миналогодишните изчертани масиви.</li>
                    <li><strong>Копирай замени</strong> — пренася миналогодишните ДЗ. Преди
                        копирането се показва <strong>списък за одобрение</strong>: за всяка
                        замяна пише дали е <em>нова</em>, дали <em>вече е отразена</em> за тази
                        година, или е <em>подобна</em> (същият ползвател и землища, но друга
                        площ). Отметнати са само новите; вече отразените не могат да се
                        отметнат. Така замяна, която вече е влязла при насрещния ползвател, не
                        се вписва втори път.</li>
                    <li>По желание може да зададете <strong>друг ползвател-източник</strong>; ако полето е
                        празно, се копира от същия ползвател.</li>
                </ul>
                <p>След копиране се показва резюме — колко записа са копирани и колко са пропуснати (напр.
                    поради липсващи ползватели за текущата година).</p>
                <p><strong>Защо замяна може да е „вече отразена".</strong> Една доброволна замяна се
                    записва наведнъж за двамата участници. Щом я копирате при единия, тя вече стои и
                    при другия — затова при втория ползвател излиза като отразена и се прескача.</p>
                <p>При <strong>изчертаните площи</strong> няма такава проверка: там повторното копиране
                    добавя редовете отново, затова се пита за потвърждение.</p>
            </Section>

            {/* 14. Reports */}
            <Section id="reports" title="14. Справки С1–С14">
                <p>Екранът <strong>„Справки"</strong> (по година) събира всички справки — всяка в собствена
                    карта, подредени по номер. Под името на всяка карта стои{" "}
                    <strong>кратко описание как точно смята</strong> справката. Всяка се извежда на
                    екран и може да се <strong>изтегли като PDF</strong> с бутона „PDF".</p>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>Справка</th>
                            <th>Какво показва</th>
                            <th>Избор</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td className="fw-bold">С1</td>
                            <td>Справка на землище — всички ползватели в едно землище с пълните им площи и
                                обща сума (ОБЩО).</td>
                            <td>Землище</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С2</td>
                            <td>Справка по ползвател за всички землища, в които има данни, с общи суми.</td>
                            <td>Ползвател</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С3</td>
                            <td>Пълна справка по ползвател за землище — площи, всички ДЗ и всички изчертани
                                масиви (с подпис). При избрана община — за всяко землище в нея, в което
                                ползвателят има декларация (дори да не е чертал там); PDF-ът е общ, по
                                землище на страница.</td>
                            <td>Ползвател + землище или община</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С4</td>
                            <td>Обща справка по ползвател — като С3, но без таблицата със замените. Също
                                и за цяла община.</td>
                            <td>Ползвател + землище или община</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С5</td>
                            <td>ДЗ кръстосана — всички замени в едно землище или в цяла община, за избран
                                ползвател.</td>
                            <td>Землище/община + ползвател</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С6</td>
                            <td>ДЗ-протоколи — общ PDF с по един протокол на замяна, за землище или община.</td>
                            <td>Землище/община + ползвател</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С7</td>
                            <td>ДЗ между <em>двама конкретни</em> ползватели — по села. В{" "}
                                <strong>ОБЩО</strong> влизат само землищата по име; неопределеното е
                                огледалният запис на същите замени и стои на свой ред. Равни рамене се
                                проверяват на реда <strong>„Всичко"</strong>, а не по разликата по села
                                (тя не е нула и при изрядни данни — виж „Съвети").</td>
                            <td>Двама ползватели</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С8</td>
                            <td>Справка по ползвател с корекция — като С3 плюс колона „Корекция". Също
                                и за цяла община.</td>
                            <td>Ползвател + землище или община</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С9</td>
                            <td>Контролна справка на землище — Правно / Годна / Изчертано / Налично на всички
                                ползватели, с равнение (тоталите се показват и най-отгоре). Под тоталите:{" "}
                                <span style={{color: "var(--app-danger-fg)", fontWeight: 600}}>червен</span> ред
                                със сбора на над-изчерталите и{" "}
                                <span style={{color: "var(--app-success-fg)", fontWeight: 600}}>зелен</span> ред
                                с оставащото за чертане. Сборът на двете е общото налично на землището —
                                нула значи, че землището е изчертано докрай.</td>
                            <td>Землище</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С10</td>
                            <td>ДЗ кръстосана по ползвател — от кого получава и на кого дава
                                ползвателят и разликата.</td>
                            <td>Ползвател</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С11</td>
                            <td>Обобщени култури по ползвател — землище / масив / МПО / площ / култура за
                                всички землища, а накрая общо по култури с проценти и торта-диаграма.</td>
                            <td>Ползвател</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С12</td>
                            <td>Замени по състояние на одобрението — чакащи, одобрени, отказани и замени
                                към неопределени землища. Полезна е най-вече за землища, които не са
                                зададени на никого: там няма кой друг да погледне какво чака.</td>
                            <td>—</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С13</td>
                            <td>БП и пътища — въведените бели петна и пътища по ползвател, със сборове
                                за всяко землище и общо за общината. Ползвател без въведени данни
                                излиза с нули, а не изчезва — така се вижда и кой още не е попълнен.</td>
                            <td>Землище или община</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">С14</td>
                            <td>Изчертани площи, текуща и предходна година — изчертаното{" "}
                                <strong>по масиви</strong> за двете стопански години и разликата със
                                знак. МПО номерата в един масив се сумират (те се преномерират между
                                годините). Масив само в едната година излиза като{" "}
                                <span style={{color: "var(--app-success-fg)", fontWeight: 600}}>нов</span> или{" "}
                                <span style={{color: "var(--app-danger-fg)", fontWeight: 600}}>отпаднал</span>.</td>
                            <td>Ползвател + землище или община</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-muted">Справки С1 и С13 са достъпни и от екрана на землището; „Пълна" (С3),
                    „Обща" (С4) и „Контролна" (С9) — от табовете на картата на ползвателя. Справките в
                    PDF на С3 и С4 носят логото на дружеството.</p>
                <p className="text-muted">Справките с таблица „изчертани площи" (С3, С4, С8, С11) носят и
                    колона <strong>„Забележка"</strong> след „Култура" — това е бележката, записана на
                    самия изчертан ред.</p>
            </Section>

            {/* 14б. Сравнение на декларации */}
            <Section id="declarations" title="15. Сравнение на декларации">
                <p>В секцията на землището табът <strong>„Сравнение на декларации"</strong> сравнява два
                    декларационни файла (минала и текуща стопанска година) — само сравнение, не пипа
                    основната база. Качете двата CSV файла; резултатът показва движенията (СД1 — смяна на
                    класификация, СД2 — по декларатор) и несъпоставените имоти.</p>
                <p>Файлът е с шапка (първи ред) точно с тези колони:</p>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>Класификация</th><th>Желание</th><th>Година</th><th>ЕКАТТЕ</th>
                            <th>Номер на имот</th><th>Площ</th><th>Булстат</th><th>Ползвател</th>
                        </tr>
                        </thead>
                    </table>
                </div>
                <ul>
                    <li><strong>Класификация:</strong> „За разпределяне" / „Не участва в разпределянето".</li>
                    <li><strong>Желание:</strong> „Обработка в реални граници" / „За участие в споразумение".</li>
                    <li>Бутонът <strong>„Свали шаблон (.csv)"</strong> сваля празен файл само с шапката.</li>
                    <li>В СД1 излизащ имот с желание „За участие в споразумение" се маркира с червено
                        „провери" — сигнал за необичайна причина за излизане.</li>
                </ul>
            </Section>

            {/* 14в. Администрация */}
            {isAdmin && (
            <Section id="admin" title="16. Администрация (само за администратори)">
                <p>Потребители с роля <strong>администратор</strong> виждат бутон{" "}
                    <strong>„Администрация"</strong> в лентата. Панелът има раздели в лявата колона:</p>
                <ul>
                    <li><strong>Табло</strong> — обобщение за годината и последни действия.</li>
                    <li><strong>Въпроси</strong> — питанията, изпратени от потребителите през последния
                        раздел на това ръководство. Значка показва колко чакат отговор.</li>
                    <li><strong>Потребители</strong> — създаване/редакция на акаунти, пароли, право
                        „Справки" и <strong>достъп по землища</strong> (празен списък = достъп до всички).</li>
                    <li><strong>Данни</strong> — зареждане/редакция/изтриване на първоначални данни.</li>
                    <li><strong>Номенклатури</strong> — активни землища (области → общини → землища) и
                        култури.</li>
                    <li><strong>Линкове</strong> — публичните линкове за ползватели и PDF документите по
                        землище.</li>
                    <li><strong>Активност</strong> — журнал на действията (кой какво е направил, кога).</li>
                    <li><strong>Система</strong> — брой записи и версия.</li>
                </ul>
                <p className="text-muted">Ограниченият по землища потребител вижда само своите землища;
                    справките изискват отделно право „Справки".</p>
            </Section>
            )}

            {/* Съвети — номерът зависи от това дали разделът „Администрация" се показва */}
            <Section id="tips" title={`${isAdmin ? 17 : 16}. Съвети и чести въпроси`}>
                <h6 className="fw-bold">Повторно качих същия файл — ще се дублират ли данните?</h6>
                <p>Не. Качването е инкрементално — вече съществуващите редове се прескачат. Ще се добавят
                    само нови редове.</p>

                <h6 className="fw-bold">Как да поправя вече качена площ?</h6>
                <p>Чрез <em>„Първоначални данни" → „Редакция на данни в базата"</em>. Повторното качване на
                    файл не променя съществуващи редове.</p>

                <h6 className="fw-bold">Защо „Налично" при изчертаване е червено?</h6>
                <p>Изчертали сте повече от правно основаната площ. Проверете ДЗ, другите площи и корекцията,
                    или намалете изчертаните площи.</p>

                <h6 className="fw-bold">Кирилицата в CSV излиза с „�" знаци.</h6>
                <p>Системата разчита и Windows-1251, и UTF-8. Ако все пак има проблем, презапишете файла с
                    кодиране UTF-8 и се уверете, че разделителят е запетая.</p>

                <h6 className="fw-bold">Как да сменя годината?</h6>
                <p>От бутона с годината най-вляво в горната лента — той е падащо меню с всички
                    години. Или през „Начална страница" в същото меню.</p>

                <h6 className="fw-bold">В С7 разликата по села не е нула. Грешка ли е?</h6>
                <p>Не. Замяната се вписва с <em>едно</em> конкретно землище, а насрещното остава
                    „неопределено" — и от коя страна е въведена, решава в коя колона ще застане
                    именуваното землище. Затова по села колоните почти винаги се разминават.
                    Равните рамене се проверяват на реда <strong>„Всичко (по села + неопределено)"</strong>:
                    там двете числа трябва да съвпаднат и точно там пише ✓ или предупреждение.</p>

                <h6 className="fw-bold">Какво значи „отпаднал" и „нов" в С14?</h6>
                <p><strong>Отпаднал</strong> — масивът е бил изчертан миналата стопанска година, а тази
                    не е. <strong>Нов</strong> — обратното. В скоби стои числото, за да се вижда колко
                    е било или колко е станало. Масив, който го има и двете години, показва обикновена
                    разлика със знак.</p>

                <h6 className="fw-bold">В С9 зеленото и червеното не са равни. Защо?</h6>
                <p>Съвпадат само когато землището е изчертано докрай (Σ правно = Σ изчертано). Сборът на
                    двете е общото налично на землището — колкото остава, толкова.</p>

                <h6 className="fw-bold">Защо при някои ползватели няма връзка към Търговския регистър?</h6>
                <p>Защото в полето „Булстат" стои ЕГН на физическо лице, а не ЕИК на фирма. По ЕГН
                    фирменият регистър няма какво да покаже, а и личният номер не бива да се праща към
                    външна търсачка. Бутонът „копирай" работи и там.</p>
            </Section>

            {/* И2: полето за въпрос е НАКРАЯ на ръководството — стига се до него,
                след като човекът е минал през написаното и не е намерил отговор. */}
            <Section id="ask" title={`${isAdmin ? 18 : 17}. Въпрос към администратора`}>
                <AskQuestion/>
            </Section>

            <hr className="mt-5"/>
            <p className="text-center text-muted">
                <Link to="/">Обратно към началото</Link>
            </p>
        </div>
    );
};
